import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../../utils/api";
import { useUser } from "../../contexts/UserContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiSearch, FiUser, FiLink, FiDownload, FiX, FiMail, FiPhone, FiActivity, FiRefreshCw } from "react-icons/fi";
import { RiFileExcel2Fill } from "react-icons/ri";
import { HiDocumentText, HiOutlineCloudUpload, HiPaperClip } from "react-icons/hi";
import ClientInquiryTable, { buildSerialColumn, renderStatusBadge } from "../../components/ClientInquiryTable";
import Loader from "../../components/Loader";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Select from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Rich text editor minimal toolbar
const minimalToolbar = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  ['link'],
  ['clean']
];

// Date Formatter Helpers
const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} • ${hours}:${minutes}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
};

// ── REUSABLE CLIPBOARD COPY COMPONENT ─────────────────────────────────────────
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation(); // Avoid triggering row clicks or details drawers
    navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy'}
      style={{ lineHeight: 0, verticalAlign: 'middle', marginLeft: '6px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      className="text-gray-700 hover:text-gray-600 transition-colors cursor-pointer"
    >
      {copied
        ? <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700 }}>✓ Copied</span>
        : <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }
    </button>
  );
};

// ── CUSTOM DROPZONE COMPONENT ────────────────────────────────────────────────
const Dropzone = ({ onFilesSelected, files, onFileRemove }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      const selected = Array.from(e.dataTransfer.files).filter(file => {
        if (file.size > 1.5 * 1024 * 1024 && !file.name.endsWith('.zip')) {
          toast.error(`${file.name} exceeds 1.5 MB limit. Please zip it.`);
          return false;
        }
        return true;
      });
      if (selected.length > 0) onFilesSelected(selected);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files).filter(file => {
        if (file.size > 1.5 * 1024 * 1024 && !file.name.endsWith('.zip')) {
          toast.error(`${file.name} exceeds 1.5 MB limit. Please zip it.`);
          return false;
        }
        return true;
      });
      if (selected.length > 0) onFilesSelected(selected);
      e.target.value = '';
    }
  };

  return (
    <div className="w-full">
      <div 
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          isDragging ? 'border-red-500 bg-red-50/30 scale-[1.01]' : 'border-gray-300 bg-slate-50/50 hover:bg-white hover:border-red-500/30'
        }`}
      >
        <input
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.pdf,.docx,.xls,.xlsx,.csv,.txt,.zip"
          onChange={handleFileChange}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
        />
        <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
          <HiOutlineCloudUpload className="text-3xl text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Drag or click to upload</span>
          <div className="text-[10px] leading-tight text-gray-500 space-y-1">
            <p>
              <span className="font-bold text-gray-700">Accepted formats:</span> png, jpg, jpeg, pdf, docx, xls, xlsx, csv, txt, zip.
            </p>
            <p>
              <span className="font-bold text-gray-700">Maximum size:</span> 1.5 MB per file. Zip files up to 3 MB are allowed.
            </p>
            <p className="italic text-red-500">
              If your file exceeds 1.5 MB, please convert it into a zip file and upload it.
            </p>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5 text-xs text-left">
          {files.map((file, idx) => (
            <li
              key={idx}
              className="flex items-center justify-between gap-2 bg-white px-3 py-2 rounded-xl border border-gray-150 shadow-sm"
            >
              <span className="truncate flex-1 text-gray-750 font-bold flex items-center gap-1.5">
                <HiPaperClip className="text-gray-400 shrink-0" /> {file.name}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFileRemove(idx);
                }}
                className="text-red-500 hover:text-red-700 text-xs font-black px-1.5"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── MAIN MODULE COMPONENT ──────────────────────────────────────────────────
export default function InquiryTab() {
  const { user } = useUser();
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("clientInquiryRows_inquiries");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [sortColumn, setSortColumn] = useState(0);
  const [sortDir, setSortDir] = useState("desc");
  const drawRef = useRef(1);

  const [stats, setStats] = useState(null);
  const [inquiryToDelete, setInquiryToDelete] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    localStorage.setItem("clientInquiryRows_inquiries", String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, rowsPerPage, startDate, endDate, customerId, assignedTo]);

  const loadInquiries = useCallback(async () => {
    setIsLoading(true);
    drawRef.current += 1;
    const start = (currentPage - 1) * rowsPerPage;
    try {
      const params = {
        draw: drawRef.current,
        start,
        length: rowsPerPage,
        search: debouncedSearch || "",
        order: [{ column: sortColumn, dir: sortDir }],
        startDate,
        endDate,
        customerId,
        assignedTo,
      };
      if (statusFilter) params.statusId = statusFilter;

      const res = await api.get("/client-inquiry", { params });
      setInquiries(res.data?.data || []);
      setTotalRows(res.data?.recordsFiltered ?? res.data?.recordsTotal ?? 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load inquiries");
      setInquiries([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, debouncedSearch, statusFilter, sortColumn, sortDir, startDate, endDate, customerId, assignedTo]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/client-inquiry/dashboard-stats");
      if (res.data) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Stats load failed:", err);
    }
  }, []);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSort = useCallback((column, sortDirection) => {
    setSortColumn(typeof column?.sortIndex === "number" ? column.sortIndex : 0);
    setSortDir(sortDirection);
    setCurrentPage(1);
  }, []);

  // Modals state
  const [showFormModal, setShowFormModal] = useState(false);
  const [detailInquiry, setDetailInquiry] = useState(null);
  
  // Form values
  const getTodayDateTimeString = () => {
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    return (new Date(now - tzOffset)).toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    inquiry_id: "",
    cust_key: "",
    customer_name: "",
    contact_person: "",
    email_id: "",
    phone_number: "",
    source: "Referral",
    requirement_summary: "",
    status_id: "1",
    assigned_to: "",
    request_date: getTodayDateTimeString(),
    next_followup_date: "",
    followup_time: "",
    quote_emails: "",
    modules: []
  });
  const [creationAttachments, setCreationAttachments] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const loadDropdowns = useCallback(async () => {
    try {
      const [empRes, custRes, statRes, modRes] = await Promise.all([
        api.get("/client-inquiry/employees"),
        api.get("/client-inquiry/customers"),
        api.get("/client-inquiry/statuses"),
        api.get("/client-inquiry/modules")
      ]);
      if (empRes.data?.status) setEmployees(empRes.data.data);
      if (custRes.data?.status) setCustomers(custRes.data.data);
      if (statRes.data?.status) setInquiryStatuses(statRes.data.inquiry_statuses);
      if (modRes.data?.status) setModuleOptions(modRes.data.data.map(m => ({ value: m.id, label: m.module_name })));
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  useEffect(() => {
    setDetailInquiry((prev) => {
      if (!prev?.inquiry_id) return prev;
      const updated = inquiries.find((i) => i.inquiry_id === prev.inquiry_id);
      return updated || prev;
    });
  }, [inquiries]);

  const fetchContacts = async (custKey) => {
    if (!custKey) {
      setContacts([]);
      return;
    }
    setIsContactsLoading(true);
    try {
      const res = await api.get("/client-inquiry/contacts", { params: { cust_key: custKey } });
      if (res.data?.status) {
        setContacts(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch customer contacts:", err);
      setContacts([]);
    } finally {
      setIsContactsLoading(false);
    }
  };

  // Handle Mapping Customer Details Selection
  const handleCustomerSelect = (key) => {
    if (!key) {
      setFormData(prev => ({
        ...prev,
        cust_key: "",
        customer_name: "",
        contact_person: "",
        email_id: "",
        phone_number: ""
      }));
      setContacts([]);
      return;
    }
    const matched = customers.find(c => String(c.cust_key) === String(key));
    if (matched) {
      setFormData(prev => ({
        ...prev,
        cust_key: matched.cust_key,
        customer_name: matched.customer_name || "",
        contact_person: "",
        email_id: "",
        phone_number: ""
      }));
      fetchContacts(matched.cust_key);
    }
  };

  const handleOpenForm = (inquiry = null) => {
    setFormErrors({});
    setCreationAttachments([]);
    if (inquiry) {
      // Parse comma-separated module IDs back into react-select option objects
      const savedModules = inquiry.modules
        ? inquiry.modules.split(',').map(id => id.trim()).filter(Boolean).map(id => {
            const found = moduleOptions.find(m => String(m.value) === String(id));
            return found || null;
          }).filter(Boolean)
        : [];
      setFormData({
        inquiry_id: inquiry.inquiry_id,
        cust_key: inquiry.cust_key || "",
        customer_name: inquiry.customer_name || "",
        contact_person: inquiry.contact_person || "",
        email_id: inquiry.email_id || "",
        phone_number: inquiry.phone_number || "",
        source: inquiry.source || "Referral",
        requirement_summary: inquiry.requirement_summary || "",
        status_id: String(inquiry.status_id),
        assigned_to: inquiry.assigned_to || "",
        request_date: inquiry.request_date ? inquiry.request_date.replace(" ", "T").slice(0, 16) : getTodayDateTimeString(),
        next_followup_date: inquiry.next_followup_date || "",
        followup_time: inquiry.followup_time || "",
        quote_emails: inquiry.quote_emails || "",
        modules: savedModules
      });
      if (inquiry.cust_key) {
        fetchContacts(inquiry.cust_key);
      } else {
        setContacts([]);
      }
    } else {
      setFormData({
        inquiry_id: "",
        cust_key: "",
        customer_name: "",
        contact_person: "",
        email_id: "",
        phone_number: "",
        source: "Referral",
        requirement_summary: "",
        status_id: "1",
        assigned_to: "",
        request_date: getTodayDateTimeString(),
        next_followup_date: "",
        followup_time: "",
        quote_emails: "",
        modules: []
      });
      setContacts([]);
    }
    setShowFormModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.customer_name?.trim()) {
      errors.customer_name = "Customer / Organization Name is required";
    }
    if (!formData.contact_person?.trim()) {
      errors.contact_person = "Contact Person Name is required";
    }
    
    if (!formData.email_id?.trim()) {
      errors.email_id = "Email Address is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email_id.trim())) {
        errors.email_id = "Please enter a valid email address";
      }
    }
    
    if (!formData.phone_number?.trim()) {
      errors.phone_number = "Phone Number is required";
    } else {
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(formData.phone_number.trim())) {
        errors.phone_number = "Phone number must be exactly 10 digits (numbers only)";
      }
    }
    
    if (!formData.request_date) {
      errors.request_date = "Request Date & Time is required";
    }
    


    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = formData.requirement_summary || "";
    const cleanText = tempDiv.textContent || tempDiv.innerText || "";
    if (!cleanText.trim()) {
      errors.requirement_summary = "Requirement Specifications Details Summary is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveInquiry = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all the required fields.");
      return;
    }

    const payload = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'modules') {
        // Serialize selected module IDs as comma-separated string
        const moduleIds = (formData.modules || []).map(m => m.value).join(',');
        payload.append('modules', moduleIds);
      } else if (formData[key] !== null && formData[key] !== undefined) {
        payload.append(key, formData[key]);
      }
    });

    creationAttachments.forEach(file => {
      payload.append("files[]", file);
    });

    const loadingToast = toast.loading("Saving inquiry...");
    try {
      const res = await api.post("/client-inquiry/save", payload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.status) {
        toast.update(loadingToast, { render: "Inquiry saved successfully!", type: "success", isLoading: false, autoClose: 3000 });
        setShowFormModal(false);
        loadInquiries();
        loadStats();
      } else {
        toast.update(loadingToast, { render: "Validation error, check fields", type: "error", isLoading: false, autoClose: 3000 });
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || "Failed to save inquiry";
      toast.update(loadingToast, { render: errMsg, type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleDeleteInquiry = (inquiry) => {
    setInquiryToDelete(inquiry);
  };

  const confirmDeleteInquiry = async () => {
    if (!inquiryToDelete) return;
    const id = inquiryToDelete.inquiry_id;
    setInquiryToDelete(null);
    const toastId = toast.loading("Deleting inquiry...");
    const payload = new FormData();
    payload.append("inquiry_id", id);

    try {
      const res = await api.post("/client-inquiry/delete", payload);
      if (res.data?.status) {
        toast.update(toastId, { render: "Inquiry deleted successfully", type: "success", isLoading: false, autoClose: 3000 });
        loadInquiries();
        loadStats();
      } else {
        toast.update(toastId, { render: "Delete failed", type: "error", isLoading: false, autoClose: 3000 });
      }
    } catch (err) {
      toast.update(toastId, { render: "Delete failed", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleExport = async () => {
    const toastId = toast.loading("Generating Excel report...");
    try {
      const params = {
        search: debouncedSearch || "",
        startDate,
        endDate,
        customerId,
        assignedTo,
      };
      if (statusFilter) params.statusId = statusFilter;

      const res = await api.get("/client-inquiry", { params });
      
      if (res.data?.status && Array.isArray(res.data.data)) {
        const data = res.data.data;
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Inquiries Report");

        sheet.columns = [
          { header: 'Inquiry ID', key: 'inquiry_id', width: 12 },
          { header: 'Customer / Organization', key: 'customer_name', width: 30 },
          { header: 'Contact Person', key: 'contact_person', width: 25 },
          { header: 'Email Address', key: 'email_id', width: 25 },
          { header: 'Phone Number', key: 'phone_number', width: 18 },
          { header: 'Source', key: 'source', width: 15 },
          { header: 'Requirement Summary', key: 'requirement_summary', width: 45 },
          { header: 'Assigned Personnel', key: 'assignee_name', width: 25 },
          { header: 'Status', key: 'status_name', width: 18 },
          { header: 'Follow-up / Comment History', key: 'followup_comments_summary', width: 60 },
          { header: 'Quote Emails', key: 'quote_emails', width: 30 },
          { header: 'Next Follow-up', key: 'next_followup', width: 22 },
          { header: 'Created Date', key: 'created_date', width: 22 }
        ];

        sheet.insertRow(1, ["eNova Software and Hardware Solutions Pvt. Ltd"]);
        sheet.insertRow(2, ["SALES PIPELINE - CLIENT INQUIRIES REPORT"]);
        sheet.insertRow(3, [`Report Date: ${new Date().toLocaleDateString('en-GB')}`]);
        sheet.insertRow(4, []); 

        sheet.mergeCells('A1:M1');
        const row1 = sheet.getRow(1);
        row1.height = 30;
        const cell1 = row1.getCell(1);
        cell1.font = { name: 'Arial', size: 18, bold: true, color: { argb: 'FFC00000' } };
        cell1.alignment = { horizontal: 'center', vertical: 'middle' };

        sheet.mergeCells('A2:M2');
        const row2 = sheet.getRow(2);
        row2.height = 25;
        const cell2 = row2.getCell(1);
        cell2.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF000000' } };
        cell2.alignment = { horizontal: 'center', vertical: 'middle' };

        sheet.mergeCells('A3:M3');
        const row3 = sheet.getRow(3);
        row3.height = 20;
        const cell3 = row3.getCell(1);
        cell3.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF666666' } };
        cell3.alignment = { horizontal: 'center', vertical: 'middle' };

        const headerRow = sheet.getRow(5);
        headerRow.height = 30;
        headerRow.eachCell((cell) => {
          cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FF000000' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });

        data.forEach(item => {
          const formattedNext = item.next_followup_date
            ? `${new Date(item.next_followup_date).toLocaleDateString('en-GB')}${item.followup_time ? ' @ ' + String(item.followup_time).slice(0, 5) : ''}`
            : '—';
          const formattedCreated = item.created_date
            ? new Date(item.created_date).toLocaleString('en-GB')
            : '—';

          // Strip HTML tags from Requirement Summary if any
          const cleanRequirementSummary = item.requirement_summary
            ? item.requirement_summary.replace(/<[^>]*>/g, '').trim()
            : 'N/A';

          const row = sheet.addRow({
            inquiry_id: item.inquiry_id,
            customer_name: item.customer_name,
            contact_person: item.contact_person,
            email_id: item.email_id,
            phone_number: item.phone_number,
            source: item.source || 'Referral',
            requirement_summary: cleanRequirementSummary,
            assignee_name: item.assignee_name || 'Unassigned',
            status_name: item.status_name,
            followup_comments_summary: item.followup_comments_summary || 'No comments logged',
            quote_emails: item.quote_emails || '—',
            next_followup: formattedNext,
            created_date: formattedCreated
          });
          
          if (!item.followup_comments_summary) {
            row.height = 25;
          }
          
          row.eachCell((cell, colNumber) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            if (colNumber === 7 || colNumber === 10) {
              cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
            } else {
              cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
          });
        });
        
        sheet.autoFilter = 'A5:M5';

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Client_Inquiries_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.update(toastId, { render: "Excel exported successfully!", type: "success", isLoading: false, autoClose: 3000 });
      } else {
        toast.update(toastId, { render: "No data found for report", type: "warning", isLoading: false, autoClose: 3000 });
      }
    } catch (err) {
      console.error("Export failed:", err);
      toast.update(toastId, { render: "Export failed", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleOpenDetail = (inquiry) => {
    setDetailInquiry(inquiry);
  };

  const handleCloseDetail = () => {
    setDetailInquiry(null);
  };

  const handleEditFromDetail = (inquiry) => {
    setDetailInquiry(null);
    handleOpenForm(inquiry);
  };

  const handleDetailRefresh = () => {
    loadInquiries();
  };

  const getStatusBadge = (statusName, statusColor) => renderStatusBadge(statusName, statusColor);

  const inquiryColumns = useMemo(() => {
    const cols = [
      buildSerialColumn(currentPage, rowsPerPage),
      {
        name: "Customer / Organization",
        sortIndex: 1,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div>
            <div className="font-bold text-gray-900 flex items-center gap-1">
              <span>{row.customer_name}</span>
              <CopyButton text={row.customer_name} />
            </div>
            {row.cust_key && (
              <div className="text-[10px] text-red-650 font-bold uppercase flex items-center gap-1 mt-0.5">
                <FiLink className="shrink-0" /> Mapped Customer Account
              </div>
            )}
            <div className="text-[10px] text-gray-400 mt-1">Source: {row.source}</div>
          </div>
        ),
      },
      {
        name: "Contact Details",
        sortIndex: 2,
        sortable: true,
        minWidth: "180px",
        cell: (row) => (
          <div>
            <div className="font-medium text-gray-700">{row.contact_person}</div>
            <div className="text-gray-500 mt-0.5">{row.email_id}</div>
            <div className="text-gray-500">{row.phone_number}</div>
          </div>
        ),
      },
      {
        name: "Follow-up Info",
        sortIndex: 3,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div className="flex items-start gap-1">
            <div 
              className="max-w-xs text-gray-600 line-clamp-2 italic"
              dangerouslySetInnerHTML={{ __html: row.requirement_summary || "No requirements summarized" }}
            />
            {row.requirement_summary && (
              <CopyButton text={row.requirement_summary.replace(/<[^>]*>/g, '').trim()} />
            )}
          </div>
        ),
      },
      {
        name: "Assignee",
        sortIndex: 4,
        sortable: true,
        width: "140px",
        cell: (row) =>
          row.assignee_name ? (
            <div className="flex items-center gap-1.5 text-gray-700 font-semibold">
              <FiUser className="text-gray-400 text-sm" /> {row.assignee_name}
            </div>
          ) : (
            <span className="text-gray-404 italic font-semibold">Unassigned</span>
          ),
      },
      {
        name: "Status",
        sortIndex: 5,
        sortable: true,
        minWidth: "180px",
        width: "180px",
        cell: (row) => getStatusBadge(row.status_name, row.status_color),
      },
      {
        name: "Next Follow-up",
        sortIndex: 6,
        sortable: true,
        minWidth: "160px",
        cell: (row) =>
          row.next_followup_date ? (
            <span className="text-gray-700 font-semibold flex items-center gap-1">
              <FiClock className="text-gray-400 text-sm shrink-0" />
              {formatDate(row.next_followup_date)}
              {row.followup_time ? ` @ ${String(row.followup_time).slice(0, 5)}` : ""}
            </span>
          ) : (
            <span className="text-gray-405 italic">None Scheduled</span>
          ),
      },
      {
        name: "Request Date",
        sortIndex: 7,
        sortable: true,
        minWidth: "160px",
        cell: (row) => (
          <span className="text-gray-700 font-semibold">
            {formatDateTime(row.request_date || row.created_date)}
          </span>
        ),
      },
    ];

    if (user.role_id != 2) {
      cols.push({
        name: "Actions",
        sortIndex: 5,
        sortable: false,
        width: "140px",
        right: true,
        cell: (row) => (
          <div className="ci-action-cell flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleOpenForm(row); }}
              className="p-2 bg-slate-50 hover:bg-slate-100 text-blue-605 rounded-lg border border-gray-200 transition"
              title="Edit Inquiry"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDeleteInquiry(row); }}
              className="p-2 bg-slate-50 hover:bg-red-50 text-red-600 rounded-lg border border-gray-200 transition"
              title="Delete"
            >
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [user.role_id, currentPage, rowsPerPage]);

  const dashboardCards = useMemo(() => {
    if (!stats?.status_distribution) return [];
    const grandTotal = stats.status_distribution.reduce((acc, s) => acc + Number(s.count), 0);
    const activeStatusCards = stats.status_distribution
      .filter(s => Number(s.count) > 0)
      .map(s => {
        const label = s.status_name;
        const slug = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        return {
          status_id: s.status_id,
          label: label,
          count: Number(s.count),
          bg: `bg-${slug}`
        };
      });

    return [
      { status_id: "", label: 'All Inquiries', count: grandTotal, bg: 'bg-all' },
      ...activeStatusCards
    ];
  }, [stats]);

  const getCardIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes('all')) return <FiActivity className="text-[60px] opacity-10" />;
    if (l.includes('new') || l.includes('init')) return <FiPlus className="text-[60px] opacity-10" />;
    if (l.includes('discussion') || l.includes('process')) return <FiClock className="text-[60px] opacity-10" />;
    return <FiActivity className="text-[60px] opacity-10" />;
  };

  const renderCard = (card) => {
    const isSelected = statusFilter === card.status_id;
    return (
      <div
        key={card.label}
        onClick={() => setStatusFilter(card.status_id)}
        className={`
          relative flex-none w-44 sm:w-52 h-[105px] p-4 cursor-pointer
          duration-300 shadow-md rounded-2xl transition-all flex flex-col justify-between ${card.bg} text-gray-850
          ${isSelected
            ? "ring-2 ring-red-500 ring-offset-2 shadow-xl scale-[1.03] z-10"
            : "hover:shadow-lg hover:scale-[1.02] hover:-translate-y-1"
          }
        `}
      >
        <div className="text-[10px] font-black relative z-[4] whitespace-normal break-words leading-tight mb-2 uppercase tracking-[0.1em] opacity-60">
          {card.label}
        </div>
        <div className="flex items-end justify-between mt-auto">
          <div className="text-3xl sm:text-4xl font-sans font-black tracking-tighter leading-none">{card.count}</div>
        </div>
        <div className="absolute right-2 bottom-2 opacity-20 pointer-events-none scale-75">
          {getCardIcon(card.label)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <ToastContainer />

      {/* Top Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2">
        <div>
          <h2 className="text-lg font-black text-gray-800 tracking-tight uppercase">Sales Pipeline</h2>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">Leads, Followups & Activity Progress</p>
        </div>
        <div className="flex items-center gap-2">
          {String(user?.role_id) !== "2" && (
            <button onClick={handleExport} className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all shadow-sm" title="Export Excel">
              <RiFileExcel2Fill className="w-5 h-5" />
            </button>
          )}
          <button onClick={() => { loadInquiries(); loadStats(); toast.success("Data refreshed"); }} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-all shadow-sm">
            <FiRefreshCw className="w-5 h-5" />
          </button>
          {String(user?.role_id) !== "2" && (
            <button onClick={() => handleOpenForm()} className="flex items-center gap-2 px-6 py-2 bg-red-700 text-white rounded-lg text-xs font-black hover:bg-red-800 transition-all uppercase tracking-wider">
              <FiPlus className="w-4 h-4" />
              New Inquiry
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {dashboardCards.length > 0 && (
        <div className="flex flex-wrap gap-4 py-2">
          {dashboardCards.map(card => renderCard(card))}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-150 shadow-sm">
        <div className="flex flex-wrap items-center gap-6">
          {/* Rows */}
          <div className="flex flex-col w-[70px]">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Rows</label>
            <select 
              className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-red-650/15"
              value={rowsPerPage}
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          {/* Created Date Range */}
          <div className="flex flex-col w-full md:w-[380px]">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Created Date Range</label>
            <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 bg-slate-50 border border-gray-200 rounded-lg p-1.5">
              <input 
                type="date" 
                className="bg-white border border-gray-100 text-[11px] font-bold text-gray-700 px-2 py-1.5 rounded outline-none flex-1 min-w-[110px]"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span className="text-gray-300 hidden md:inline">→</span>
              <input 
                type="date" 
                className="bg-white border border-gray-100 text-[11px] font-bold text-gray-700 px-2 py-1.5 rounded outline-none flex-1 min-w-[110px]"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <button 
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setStartDate(today);
                  setEndDate(today);
                }}
                className="px-3 py-1.5 text-[9px] font-black text-red-700 hover:bg-red-50 rounded uppercase border border-red-100 bg-white shadow-sm whitespace-nowrap active:scale-95 transition-all"
              >Today</button>
              {(startDate || endDate) && (
                <button 
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="px-2 py-1.5 text-[9px] font-bold text-gray-500 hover:bg-gray-100 rounded uppercase border bg-white"
                >Clear</button>
              )}
            </div>
          </div>

          {/* Mapped Customer */}
          {String(user?.role_id) !== "2" && (
            <div className="flex flex-col w-full sm:w-[180px]">
              <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Mapped Customer</label>
              <select 
                className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-red-650/15"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">All Customers</option>
                {customers.map(c => <option key={c.cust_key} value={c.cust_key}>{c.customer_name}</option>)}
              </select>
            </div>
          )}

          {/* Assignee */}
          <div className="flex flex-col w-full sm:w-[160px]">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Assignee</label>
            <select 
              className="bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-red-650/15"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <option value="">All Personnel</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          {/* Search leads */}
          <div className="flex flex-col w-full md:w-[200px]">
            <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Search Leads</label>
            <input 
              type="text" 
              placeholder="Search..." 
              className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-red-650/15 shadow-inner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Main Grid Table */}
      <ClientInquiryTable
        columns={inquiryColumns}
        data={inquiries}
        isLoading={isLoading}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onSort={handleSort}
        onRowClicked={handleOpenDetail}
        keyField="inquiry_id"
        emptyMessage="No active inquiries found"
      />

      {/* Details Slide Drawer */}
      {detailInquiry && (
        <InquiryDetailSlide
          inquiry={detailInquiry}
          inquiryStatuses={inquiryStatuses}
          employees={employees}
          moduleOptions={moduleOptions}
          canEdit={user.role_id != 2}
          onClose={handleCloseDetail}
          onEdit={handleEditFromDetail}
          onRefreshList={handleDetailRefresh}
        />
      )}

      {/* Delete Confirmation Modal */}
      {inquiryToDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-red-50">
            <div className="p-8 text-center space-y-4">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-2 animate-bounce">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-black text-gray-805 tracking-tight">Are you sure?</h3>
              <p className="text-sm text-gray-505 font-medium leading-relaxed">
                You are about to delete inquiry for <span className="text-red-655 font-bold">"{inquiryToDelete.customer_name}"</span>. This action cannot be undone.
              </p>
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => setInquiryToDelete(null)}
                  className="flex-1 py-3 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmDeleteInquiry}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition-all uppercase tracking-widest shadow-lg shadow-red-200"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CREATE & EDIT FORM MODAL (Upgraded Ticket UI style) */}
      {showFormModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-4xl bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
            
            {/* Absolute close button */}
            <button
              onClick={() => setShowFormModal(false)}
              className="absolute top-5 right-5 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
              aria-label="Close"
            >
              <FiX className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="p-6 border-b shrink-0">
              <h2 className="text-2xl font-semibold text-center text-red-600">
                {formData.inquiry_id ? "Edit Client Inquiry" : "Create New Inquiry"}
              </h2>
              <p className="text-sm text-center text-gray-500 mt-1">
                Fill in the details below to log a client inquiry.
              </p>
            </div>
            
            <form onSubmit={handleSaveInquiry} noValidate className="px-6 py-6 space-y-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                
                {/* Associate customer profile */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Associate Existing Client Profile (Optional)
                  </label>
                  <Select
                    name="cust_key"
                    options={customers.map(c => ({ value: c.cust_key, label: c.customer_name }))}
                    value={
                      formData.cust_key 
                        ? { value: formData.cust_key, label: customers.find(c => String(c.cust_key) === String(formData.cust_key))?.customer_name || "" }
                        : null
                    }
                    placeholder="Select Client"
                    onChange={(selected) => {
                      const key = selected ? selected.value : "";
                      handleCustomerSelect(key);
                    }}
                    isClearable
                    className="text-sm text-left"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Organization name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {formData.cust_key ? "Customer / Organization Name *" : "New Customer / Organization Name *"}
                  </label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    disabled={!!formData.cust_key}
                    onChange={(e) => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                    className={`block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-650/15 ${formData.cust_key ? "bg-slate-100 cursor-not-allowed text-gray-500" : "bg-white"}`}
                  />
                  {formErrors.customer_name && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.customer_name}</p>
                  )}
                </div>

                {/* Contact person */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Contact Person Name *
                  </label>
                  {formData.cust_key ? (
                    <Select
                      name="contact_person"
                      options={contacts.map(c => ({ value: c.customer_name, label: c.customer_name }))}
                      value={
                        formData.contact_person
                          ? { value: formData.contact_person, label: formData.contact_person }
                          : null
                      }
                      placeholder={isContactsLoading ? "Loading contacts..." : "Select Contact Person"}
                      isLoading={isContactsLoading}
                      onChange={(selected) => {
                        if (!selected) {
                          setFormData(p => ({ ...p, contact_person: "", email_id: "", phone_number: "" }));
                          return;
                        }
                        const matched = contacts.find(c => String(c.customer_name) === String(selected.value));
                        setFormData(p => ({
                          ...p,
                          contact_person: selected.value,
                          email_id: matched ? (matched.email || "") : "",
                          phone_number: matched ? (matched.PhoneNumber || "") : ""
                        }));
                      }}
                      isClearable
                      className="text-sm text-left"
                      classNamePrefix="react-select"
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={(e) => setFormData(p => ({ ...p, contact_person: e.target.value }))}
                      className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                    />
                  )}
                  {formErrors.contact_person && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.contact_person}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email_id}
                    onChange={(e) => setFormData(p => ({ ...p, email_id: e.target.value }))}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                  {formErrors.email_id && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.email_id}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    value={formData.phone_number}
                    onChange={(e) => {
                      const val = e.target.value;
                      const cleanVal = val.replace(/\D/g, "").slice(0, 10);
                      setFormData(p => ({ ...p, phone_number: cleanVal }));
                    }}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                  {formErrors.phone_number && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.phone_number}</p>
                  )}
                </div>

                {/* Request Date & Time */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Request Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.request_date}
                    onChange={(e) => setFormData(p => ({ ...p, request_date: e.target.value }))}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                  {formErrors.request_date && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.request_date}</p>
                  )}
                </div>



                {/* Next followup date */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Next Follow-up Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.next_followup_date}
                    onChange={(e) => setFormData(p => ({ ...p, next_followup_date: e.target.value }))}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                </div>

                {/* Next followup time */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Next Follow-up Time
                  </label>
                  <input
                    type="time"
                    value={formData.followup_time}
                    onChange={(e) => setFormData(p => ({ ...p, followup_time: e.target.value }))}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                </div>

                {/* Lead source */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Lead Source
                  </label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) => setFormData(p => ({ ...p, source: e.target.value }))}
                    placeholder="Website, Referral, Event..."
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  />
                </div>

                {/* Inquiry status */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Inquiry Status
                  </label>
                  <select
                    value={formData.status_id}
                    onChange={(e) => setFormData(p => ({ ...p, status_id: e.target.value }))}
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-650/15"
                  >
                    {inquiryStatuses.map(s => <option key={s.id} value={s.id}>{s.status_name}</option>)}
                  </select>
                </div>

                {/* Assign to Employee */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Assigned Personnel Follow-up Lead
                  </label>
                  <Select
                    name="assigned_to"
                    options={employees.map(e => ({ value: e.id, label: e.name }))}
                    value={
                      formData.assigned_to
                        ? { value: formData.assigned_to, label: employees.find(e => String(e.id) === String(formData.assigned_to))?.name || "" }
                        : null
                    }
                    placeholder="Select Assignee"
                    onChange={(selected) => {
                      setFormData(p => ({ ...p, assigned_to: selected ? selected.value : "" }));
                    }}
                    isClearable
                    className="text-sm text-left"
                    classNamePrefix="react-select"
                  />
                </div>

                {/* Modules multi-select */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Modules
                  </label>
                  <Select
                    isMulti
                    name="modules"
                    options={moduleOptions}
                    value={formData.modules}
                    placeholder="Select modules asked by the client..."
                    onChange={(selected) => {
                      setFormData(p => ({ ...p, modules: selected || [] }));
                    }}
                    className="text-sm text-left"
                    classNamePrefix="react-select"
                    closeMenuOnSelect={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>

                {/* Requirement specs rich description */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium mb-1">
                    Requirement Specifications Details Summary
                  </label>
                  <ReactQuill
                    value={formData.requirement_summary}
                    onChange={(val) => setFormData(p => ({ ...p, requirement_summary: val }))}
                    placeholder="Briefly describe the requirements..."
                    modules={{ toolbar: minimalToolbar }}
                    className="custom-quill rounded-md border border-gray-305 bg-white text-gray-700"
                  />
                  {formErrors.requirement_summary && (
                    <p className="text-red-500 text-xs mt-1 font-semibold">{formErrors.requirement_summary}</p>
                  )}
                </div>

                {/* Attachments drag & drop zone */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium mb-1">Attachments</label>
                  <Dropzone
                    files={creationAttachments}
                    onFilesSelected={(newFiles) => setCreationAttachments(prev => [...prev, ...newFiles])}
                    onFileRemove={(idx) => setCreationAttachments(prev => prev.filter((_, i) => i !== idx))}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-150 uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-red-600 text-white rounded-lg text-xs font-black hover:bg-red-750 transition uppercase tracking-wider active:scale-95 shadow-md shadow-red-600/10"
                >
                  Save Inquiry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DETAIL SLIDE DRAWER COMPONENT ────────────────────────────────────────────

function InquiryDetailSlide({ inquiry, inquiryStatuses, employees = [], moduleOptions = [], canEdit, onClose, onEdit, onRefreshList }) {
  const { user } = useUser();
  const [followups, setFollowups] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [followupNotes, setFollowupNotes] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [followupTime, setFollowupTime] = useState("");
  const [followupStatusId, setFollowupStatusId] = useState("2");
  const [followupAssignedTo, setFollowupAssignedTo] = useState("");
  
  // Status update file attachments
  const [followupAttachments, setFollowupAttachments] = useState([]);

  // Versioning popup/form states
  const [showVersionPopup, setShowVersionPopup] = useState(false);
  const [versionForm, setVersionForm] = useState({
    version_number: '',
    notes: '',
    previous_changes_doc: null,
    updated_doc: null
  });
  const [submitting, setSubmitting] = useState(false);

  const [drawerWidth, setDrawerWidth] = useState(920);
  const [isResizing, setIsResizing] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const startResizing = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const stopResizing = () => {
    setIsResizing(false);
  };

  const resize = (e) => {
    if (isResizing) {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth > 500 && newWidth < (window.innerWidth - 100)) {
        setDrawerWidth(newWidth);
      }
    }
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    } else {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  const loadFollowups = useCallback(async () => {
    if (!inquiry?.inquiry_id) return;
    setLoadingLogs(true);
    try {
      const payload = new FormData();
      payload.append("inquiry_id", String(inquiry.inquiry_id));
      payload.append("notes", "");
      const res = await api.post("/client-inquiry/followup", payload);
      if (res.data?.status && Array.isArray(res.data.followups)) {
        setFollowups(res.data.followups);
      } else {
        setFollowups([]);
      }
    } catch (err) {
      toast.error("Failed to load follow-up timeline");
      setFollowups([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [inquiry?.inquiry_id]);

  useEffect(() => {
    if (!inquiry) return;
    setFollowupNotes("");
    setNextDate("");
    setFollowupTime("");
    setFollowupStatusId(String(inquiry.status_id || "1"));
    setFollowupAssignedTo(inquiry.assigned_to ? String(inquiry.assigned_to) : "");
    setFollowupAttachments([]);
    loadFollowups();
  }, [inquiry, loadFollowups]);

  const handleDownload = (filePath, fileName) => {
    const token = localStorage.getItem("token") || "";
    const downloadUrl = `${api.defaults.baseURL}/client-inquiry/download?file=${encodeURIComponent(filePath)}&name=${encodeURIComponent(fileName)}&token=${encodeURIComponent(token)}`;
    window.open(downloadUrl, "_blank");
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    if (!followupNotes.trim()) return;

    const payload = new FormData();
    payload.append("inquiry_id", inquiry.inquiry_id);
    payload.append("notes", followupNotes);
    payload.append("next_followup_date", nextDate);
    payload.append("next_followup_time", followupTime || "");
    payload.append("status_id", followupStatusId);
    payload.append("assigned_to", followupAssignedTo);
    
    // Attach files exactly like submitting ticket comment responses
    followupAttachments.forEach(file => {
      payload.append("files[]", file);
    });

    const loadingToast = toast.loading("Logging follow-up...");
    try {
      const res = await api.post("/client-inquiry/followup", payload, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.status) {
        toast.update(loadingToast, { render: "Follow-up registered!", type: "success", isLoading: false, autoClose: 3000 });
        setFollowups(res.data.followups || []);
        setFollowupNotes("");
        setNextDate("");
        setFollowupTime("");
        setFollowupAttachments([]);
        onRefreshList?.();
      }
    } catch (err) {
      toast.update(loadingToast, { render: err.response?.data?.error || "Failed to add follow-up", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  // Requirement Version update push submission
  const handleVersionSubmit = async () => {
    if (!versionForm.version_number) return toast.error("Version number is required");
    
    setSubmitting(true);
    const toastId = toast.loading("Saving version details...");
    try {
      const fd = new FormData();
      fd.append('inquiry_id', inquiry.inquiry_id);
      fd.append('version_number', versionForm.version_number);
      fd.append('notes', versionForm.notes);
      if (versionForm.previous_changes_doc) fd.append('previous_changes_doc', versionForm.previous_changes_doc);
      if (versionForm.updated_doc) fd.append('updated_doc', versionForm.updated_doc);

      const res = await api.post('/client-inquiry/add-version', fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.status) {
        toast.update(toastId, { render: "Version created successfully!", type: "success", isLoading: false, autoClose: 3000 });
        setShowVersionPopup(false);
        setVersionForm({ version_number: '', notes: '', previous_changes_doc: null, updated_doc: null });
        loadFollowups();
        onRefreshList?.();
      }
    } catch (err) {
      toast.update(toastId, { render: "Error saving version update", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVersion = async (versionLogId) => {
    const toastId = toast.loading("Deleting version entry...");
    try {
      const res = await api.post('/client-inquiry/delete-version', { id: versionLogId });
      if (res.data.status) {
        toast.update(toastId, { render: "Version entry deleted successfully", type: "success", isLoading: false, autoClose: 3000 });
        loadFollowups();
        onRefreshList?.();
      }
    } catch (err) {
      toast.update(toastId, { render: "Failed to delete version entry", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  if (!inquiry) return null;

  return createPortal(
    <div 
      className="fixed top-0 left-0 w-screen h-screen z-[99999] flex justify-end bg-black/60 backdrop-blur-[3px]"
      style={{ margin: 0, padding: 0 }}
      onClick={onClose}
    >
      <div 
        className="relative h-full bg-white shadow-[-15px_0_30px_rgba(0,0,0,0.15)] flex flex-col animate-in slide-in-from-right duration-300"
        style={{ width: `${drawerWidth}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Resize Handle */}
        <div 
          onMouseDown={startResizing}
          className="absolute left-0 top-0 w-3 h-full cursor-ew-resize hover:bg-main/5 transition-all z-20 group"
          title="Drag to resize"
        >
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
            <div className="w-1.5 h-20 bg-main rounded-full shadow-[0_0_10px_rgba(255,0,0,0.3)] group-hover:h-24 transition-all duration-300"></div>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white px-8 py-5 border-b flex items-center justify-between sticky top-0 z-10 shadow-sm shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-x-3 mb-2 flex-wrap">
              <span className="bg-red-700 text-white text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shrink-0 flex items-center shadow-md shadow-red-700/20">
                Inquiry #{inquiry.inquiry_id}
              </span>
              {inquiry.cust_key && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full uppercase tracking-widest">
                  <FiLink className="shrink-0" /> Mapped Customer Account
                </span>
              )}
            </div>
            <h2 className="text-2xl font-black text-gray-805 leading-tight tracking-tight">{inquiry.customer_name}</h2>
          </div>
          <div className="flex items-center gap-3 ml-4 shrink-0">
            {canEdit && (
              <button 
                type="button" 
                onClick={() => onEdit?.(inquiry)} 
                className="px-4 py-2 bg-white border border-gray-200 text-blue-650 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-blue-450 hover:bg-blue-50 transition-all shadow-md shadow-blue-50/50"
                title="Edit Inquiry"
              >
                Edit Inquiry
              </button>
            )}
            <button 
              onClick={onClose} 
              className="p-3 hover:bg-gray-150 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:rotate-90 transition-all duration-300"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Lead Details */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4 animate-in fade-in duration-200 text-left">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 border-b pb-2 leading-none">
              <FiUser className="text-red-700 text-sm" /> Inquiry Details
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
              <SidebarRows label="Contact Person" value={inquiry.contact_person} />
              <SidebarRows label="Email Address" value={<a href={`mailto:${inquiry.email_id}`} className="text-blue-605 hover:underline break-all leading-normal">{inquiry.email_id}</a>} />
              <SidebarRows 
                label="Phone Number" 
                value={
                  <span className="flex items-center gap-1">
                    <span>{inquiry.phone_number}</span>
                    {inquiry.phone_number && (
                      <CopyButton text={inquiry.phone_number} />
                    )}
                  </span>
                } 
              />
              <SidebarRows label="Lead Source" value={inquiry.source} />
              <SidebarRows label="Assignee PoC" value={inquiry.assignee_name ? <span className="font-bold text-gray-850 flex items-center gap-1.5"><FiUser className="shrink-0 text-red-750" />{inquiry.assignee_name}</span> : <span className="text-gray-405 italic">Unassigned</span>} />
              <SidebarRows label="Request Date" value={formatDateTime(inquiry.request_date || inquiry.created_date)} />
              <SidebarRows label="Next Follow-up" value={inquiry.next_followup_date ? `${formatDate(inquiry.next_followup_date)}${inquiry.followup_time ? ' @ ' + String(inquiry.followup_time).slice(0, 5) : ''}` : <span className="text-gray-405 italic">None Scheduled</span>} />
              <SidebarRows label="Created Date" value={formatDateTime(inquiry.created_date)} />
            </div>
          </div>

          {/* Modules Section */}
          {inquiry.modules && inquiry.modules.trim() !== '' && (() => {
            const moduleIds = inquiry.modules.split(',').map(id => id.trim()).filter(Boolean);
            const resolvedModules = moduleIds.map(id => {
              const found = moduleOptions.find(m => String(m.value) === String(id));
              return found ? found.label : null;
            }).filter(Boolean);
            if (resolvedModules.length === 0) return null;
            return (
              <div className="bg-white rounded-2xl border border-gray-100 px-6 py-4 shadow-sm text-left">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 border-b pb-2 leading-none mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10" />
                  </svg>
                  Modules Requested
                </h3>
                <div className="flex flex-wrap gap-2">
                  {resolvedModules.map((name, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm text-left">
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
              
              {/* Requirements Summary (70%) */}
              <div className="lg:col-span-7 space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 leading-none border-b pb-2">
                  <FiActivity /> Inquiry Requirements Summary
                </h3>
                <div className={`relative transition-all duration-500 overflow-hidden ${!isDescExpanded ? 'max-h-[160px]' : 'max-h-full'}`}>
                  <div 
                    className="text-sm text-gray-600 leading-relaxed ql-editor-view whitespace-normal break-words" 
                    dangerouslySetInnerHTML={{ __html: inquiry.requirement_summary || "No requirements summarized yet." }} 
                  />
                  {inquiry.requirement_summary && (
                    <div className="mt-2 text-right">
                      <CopyButton text={inquiry.requirement_summary.replace(/<[^>]*>/g, '').trim()} />
                    </div>
                  )}
                  {!isDescExpanded && inquiry.requirement_summary?.length > 350 && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                </div>
                {inquiry.requirement_summary?.length > 350 && (
                  <div className="pt-2 border-t border-gray-50 flex justify-start">
                    <button 
                      onClick={() => setIsDescExpanded(!isDescExpanded)}
                      className="text-[10px] font-black uppercase tracking-[0.2em] text-red-750 hover:text-red-800 transition-colors flex items-center gap-1.5"
                    >
                      {isDescExpanded ? 'Show Less' : 'Read Full Requirement'}
                    </button>
                  </div>
                )}
              </div>

              {/* Version History (30%) */}
              <div className="lg:col-span-3 lg:border-l border-gray-100 lg:pl-6 flex flex-col justify-start space-y-4">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 border-b pb-2 leading-none">
                  <HiDocumentText className="text-sm text-red-600" /> Version History
                </h4>
                <div>
                  <button 
                    type="button"
                    onClick={() => setShowVersionPopup(true)}
                    className="w-full flex items-center justify-between gap-3 p-4 bg-white hover:bg-red-750 rounded-xl border border-gray-250 hover:border-red-750 shadow-sm transition-all group hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-red-50 text-red-700 rounded-lg group-hover:bg-red-100 transition-all">
                        <FiActivity className="text-xl animate-pulse" />
                      </div>
                      <div className="text-left">
                        <p className="text-[10px] font-black uppercase tracking-widest leading-none">View Full History</p>
                        <p className="text-[9px] font-bold opacity-60 group-hover:opacity-85 mt-1">
                          {followups.filter(f => f.version_number).length || 0} Versions logged
                        </p>
                      </div>
                    </div>
                    <FiClock className="text-lg opacity-40 group-hover:opacity-100 transition-all shrink-0" />
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Log New Interaction Form & Version History Card Container */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 leading-none border-b pb-2 text-left">
              <FiClock /> {canEdit ? "Log New Interaction" : "Interaction Details & History"}
            </h3>
            
            {/* If canEdit, we expand the form to full width and render Discussion Details side-by-side with Attachments at the bottom */}
            {canEdit ? (
              <form onSubmit={handleAddFollowup} className="w-full space-y-4 text-left">
                {/* Next follow-up Date/Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Next Follow-up Date</label>
                    <input 
                      type="date" 
                      value={nextDate} 
                      onChange={(e) => setNextDate(e.target.value)} 
                      className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-red-650/15" 
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Next Follow-up Time</label>
                    <input 
                      type="time" 
                      value={followupTime} 
                      onChange={(e) => setFollowupTime(e.target.value)} 
                      className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-red-650/15" 
                    />
                  </div>
                </div>

                {/* Status and Assign To */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Update Status</label>
                    <select 
                      value={followupStatusId} 
                      onChange={(e) => setFollowupStatusId(e.target.value)} 
                      className="block w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-red-650/15"
                    >
                      {inquiryStatuses.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1 text-left">
                    <label className="text-sm font-medium text-gray-700">Assign To</label>
                    <Select
                      name="followup_assigned_to"
                      options={employees.map(e => ({ value: e.id, label: e.name }))}
                      value={
                        followupAssignedTo
                          ? { value: followupAssignedTo, label: employees.find(e => String(e.id) === String(followupAssignedTo))?.name || "" }
                          : null
                      }
                      placeholder="Select Assignee"
                      onChange={(selected) => {
                        setFollowupAssignedTo(selected ? selected.value : "");
                      }}
                      isClearable
                      className="text-sm text-left"
                      classNamePrefix="react-select"
                      menuPlacement="auto"
                    />
                  </div>
                </div>

                {/* Bottom Row: Discussion Details (Left) and Attachments (Right) side-by-side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start pt-2">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Discussion Details *</label>
                    <ReactQuill
                      value={followupNotes}
                      onChange={setFollowupNotes}
                      placeholder="Enter discussion details..."
                      modules={{ toolbar: minimalToolbar }}
                      className="custom-quill rounded-md border border-gray-300 bg-white text-gray-750"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">Attachments</label>
                    <Dropzone
                      files={followupAttachments}
                      onFilesSelected={(newFiles) => setFollowupAttachments(prev => [...prev, ...newFiles])}
                      onFileRemove={(idx) => setFollowupAttachments(prev => prev.filter((_, i) => i !== idx))}
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 bg-red-600 hover:bg-red-750 text-white rounded-lg text-xs font-black uppercase tracking-wider shadow-md shadow-red-200 active:scale-95 transition-all"
                  >
                    Submit Response
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-center border border-dashed border-gray-200 rounded-2xl p-8 bg-slate-50/50">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">No edit permissions for this lead</p>
              </div>
            )}
          </div>

          {/* Follow-up Timeline & Activity History */}
          <div className="space-y-6 pt-6 border-t border-gray-100 text-left">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-4 leading-none">
              <FiActivity className="text-red-700" /> Follow-up Timeline & Activity History
            </h3>
            {loadingLogs ? (
              <div className="flex justify-center py-10"><Loader /></div>
            ) : followups.length === 0 ? (
              <div className="text-center py-8 text-gray-400 font-bold text-xs uppercase bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                No follow-ups recorded yet
              </div>
            ) : (
              <div className="space-y-6">
                {followups.filter(log => log.event_type !== "init").map((log, idx) => {
                  const isVersion = log.event_type === "version";
                  
                  let circleColorClass = "bg-red-50 text-red-700 border-red-100";
                  let circleIcon = <FiUser className="w-4 h-4" />;
                  
                  if (isVersion) {
                    circleColorClass = "bg-orange-50 text-orange-605 border-orange-100";
                    circleIcon = <HiDocumentText className="w-4 h-4" />;
                  }
                  
                  return (
                    <div key={log.id || idx} className="flex gap-4 relative w-full text-left">
                      {/* Timeline Line */}
                      <div className="absolute left-[15px] top-[40px] bottom-[-24px] w-[2px] bg-gray-100" />
                      
                      {/* Icon Circle */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 overflow-hidden border ${circleColorClass} shadow-sm`}>
                        {circleIcon}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 space-y-3 pb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-gray-800">{log.creator_name || "System"}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                            {formatDateTime(log.created_date)}
                          </span>
                        </div>
                        
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 relative group hover:border-red-500/20 transition-all w-full">
                          {/* Status and Next Followup badges */}
                           <div className="flex flex-wrap gap-2 mb-3">
                             {log.status_id && (
                               <span className="px-3 py-1 rounded-full border border-blue-500 text-blue-500 bg-blue-50/10 text-[10px] font-bold uppercase tracking-tight">
                                 Status: {inquiryStatuses.find(s => s.id == log.status_id)?.status_name || 'Update'}
                               </span>
                             )}
                             {(log.assignee_name || inquiry.assignee_name) && (
                               <span className="px-3 py-1 rounded-full border border-emerald-500 text-emerald-500 bg-emerald-50/10 text-[10px] font-bold uppercase tracking-tight">
                                 Assigned To: {log.assignee_name || inquiry.assignee_name}
                               </span>
                             )}
                             {log.version_number && (
                               <span className="px-3 py-1 rounded-full border border-purple-500 text-purple-650 bg-purple-50/10 text-[10px] font-black uppercase tracking-tight">
                                 Version: V{log.version_number}
                               </span>
                             )}
                             {log.next_followup_date && (
                               <span className="px-3 py-1 rounded-full border border-orange-500 text-orange-600 bg-orange-50/10 text-[10px] font-bold uppercase tracking-tight flex items-center gap-1">
                                 <FiClock className="w-3 h-3" /> Next Follow-up: {formatDate(log.next_followup_date)}{log.next_followup_time ? ` @ ${String(log.next_followup_time).slice(0, 5)}` : ""}
                               </span>
                             )}
                           </div>
                          
                          {/* Render HTML content properly */}
                          <div className="text-sm text-gray-705 leading-relaxed whitespace-normal break-words ql-editor-view flex items-start gap-1 justify-between w-full">
                            <div 
                              className="flex-1"
                              dangerouslySetInnerHTML={{ __html: log.notes }} 
                            />
                            {log.notes && (
                              <CopyButton text={log.notes.replace(/<[^>]*>/g, '').trim()} />
                            )}
                          </div>

                          {/*Attachments — show for all event types including init*/}
                          {log.attachments?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Attachments:</div>
                              <ul className="space-y-1.5 text-left">
                                {log.attachments.map((file, fIdx) => (
                                  <li key={fIdx} className="flex items-center gap-2 text-xs font-bold">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-655 shrink-0" />
                                    <button 
                                      type="button" 
                                      onClick={() => handleDownload(file.file_path, file.file_name)}
                                      className="text-red-600 hover:text-red-805 hover:underline text-left font-bold"
                                      title={`Download ${file.file_name}`}
                                    >
                                      {file.file_name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* REQUIREMENT VERSION POPUP MODAL */}
      {showVersionPopup && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
          <div className="bg-slate-50 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            <div className="bg-white px-6 py-4 border-b flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-red-100 text-red-650 rounded-2xl">
                  <HiDocumentText className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-gray-850 tracking-tight">Version History</h3>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Historical comparison & updates</p>
                </div>
              </div>
              <button onClick={() => setShowVersionPopup(false)} className="p-2 hover:bg-gray-150 rounded-full transition-colors text-gray-400">
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-[400px]">
              {/* Timeline list of previous versions (60%) */}
              <div className="flex-1 p-6 overflow-y-auto bg-white">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2 leading-none">
                  <FiActivity className="text-red-700" /> Previous Versions
                </h4>
                <div className="space-y-4">
                  {followups.filter(f => f.version_number).length === 0 ? (
                    <div className="text-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                      <HiDocumentText className="text-4xl text-gray-200 mx-auto mb-3" />
                      <p className="text-sm text-gray-400 font-bold uppercase tracking-widest italic">No version history found</p>
                    </div>
                  ) : (
                    followups.filter(f => f.version_number).map((v, i) => {
                      const prevChanges = v.attachments?.find(a => a.doc_type === 'previous_changes');
                      const updatedDoc = v.attachments?.find(a => a.doc_type === 'updated_doc');

                      return (
                        <div key={v.id || i} className={`p-6 rounded-2xl border transition-all ${i === 0 ? 'bg-gradient-to-br from-white to-red-50/20 border-red-200 ring-2 ring-red-50/50' : 'bg-gray-50/50 border-gray-100'}`} style={{ textDecoration: 'none' }}>
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${i === 0 ? 'bg-red-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                <span className="text-xs font-black italic">V</span>
                              </div>
                              <div>
                                <span className="text-base font-black text-gray-850 tracking-tight">Version V{v.version_number}</span>
                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">{formatDateTime(v.created_date)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {i === 0 && <span className="text-[9px] font-black text-red-650 bg-red-50 px-3 py-1 rounded-full uppercase tracking-widest border border-red-200">Active Specification</span>}
                              {user.role_id == 1 && (
                                <button 
                                  onClick={() => handleDeleteVersion(v.id)}
                                  className="p-1.5 text-gray-450 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                  title="Delete Version Entry"
                                >
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div 
                            className="text-xs text-gray-650 leading-relaxed mb-4 whitespace-normal break-words ql-editor-view"
                            dangerouslySetInnerHTML={{ __html: v.notes?.replace(/^\[REQUIREMENT VERSION PUSH\].*?\nChangelog:\s*/s, '') }}
                          />

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {prevChanges && (
                              <button 
                                onClick={() => handleDownload(prevChanges.file_path, prevChanges.file_name)}
                                className="flex flex-col gap-1.5 p-4 bg-white rounded-2xl border border-orange-100 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-500/10 transition-all text-left group"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <span className="text-[9px] font-black text-orange-655 bg-orange-50 px-2 py-1 rounded uppercase tracking-widest">Modification Log</span>
                                  <HiOutlineCloudUpload className="text-orange-400 group-hover:scale-125 transition-transform" />
                                </div>
                                <span className="text-xs font-bold text-gray-700 mt-1 truncate w-full">{prevChanges.file_name}</span>
                              </button>
                            )}
                            {updatedDoc && (
                              <button 
                                onClick={() => handleDownload(updatedDoc.file_path, updatedDoc.file_name)}
                                className="flex flex-col gap-1.5 p-4 bg-white rounded-2xl border border-emerald-100 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/10 transition-all text-left group"
                              >
                                <div className="flex items-center justify-between w-full">
                                  <span className="text-[9px] font-black text-emerald-650 bg-emerald-50 px-2 py-1 rounded uppercase tracking-widest">Updated Spec</span>
                                  <HiOutlineCloudUpload className="text-emerald-400 group-hover:scale-125 transition-transform" />
                                </div>
                                <span className="text-xs font-bold text-gray-700 mt-1 truncate w-full">{updatedDoc.file_name}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Push Update version form (40%) */}
              {user.role_id != 2 && (
                <div className="w-full md:w-[380px] p-8 bg-slate-100 border-l border-gray-200 overflow-y-auto">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-red-700 text-white rounded-2xl shadow-lg shadow-red-700/30">
                      <HiOutlineCloudUpload className="text-2xl" />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-gray-805 tracking-tight">Push Update</h4>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Apply Next Version</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-550 uppercase tracking-widest pl-1">New Version Identifier *</label>
                      <input 
                        className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-red-650/10 focus:border-red-650 transition-all"
                        placeholder="e.g. V1.1"
                        value={versionForm.version_number}
                        onChange={e => setVersionForm(prev => ({ ...prev, version_number: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-550 uppercase tracking-widest pl-1">Changelog / Description *</label>
                      <ReactQuill
                        value={versionForm.notes}
                        onChange={val => setVersionForm(prev => ({ ...prev, notes: val }))}
                        placeholder="What changed in this version..."
                        modules={{ toolbar: minimalToolbar }}
                        className="custom-quill rounded-md border border-gray-300 bg-white"
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-3xl cursor-pointer hover:bg-white hover:border-orange-400 transition-all group ${versionForm.previous_changes_doc ? 'bg-orange-50 border-orange-400' : ''}`}>
                          <HiOutlineCloudUpload className={`text-3xl ${versionForm.previous_changes_doc ? 'text-orange-655' : 'text-gray-400'}`} />
                          <span className="text-[9px] font-black text-gray-500 uppercase group-hover:text-orange-600 text-center mt-2">Previous Changes</span>
                          {versionForm.previous_changes_doc && <span className="text-[8px] text-orange-850 mt-2 font-bold truncate w-full text-center px-4">{versionForm.previous_changes_doc.name}</span>}
                          <input type="file" className="hidden" onChange={e => setVersionForm(prev => ({ ...prev, previous_changes_doc: e.target.files[0] }))} />
                        </label>
                      </div>
                      <div className="space-y-1">
                        <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-3xl cursor-pointer hover:bg-white hover:border-emerald-400 transition-all group ${versionForm.updated_doc ? 'bg-emerald-50 border-emerald-400' : ''}`}>
                          <HiOutlineCloudUpload className={`text-3xl ${versionForm.updated_doc ? 'text-emerald-650' : 'text-gray-400'}`} />
                          <span className="text-[9px] font-black text-gray-500 uppercase group-hover:text-emerald-600 text-center mt-2">Updated Final Doc</span>
                          {versionForm.updated_doc && <span className="text-[8px] text-emerald-800 mt-2 font-bold truncate w-full text-center px-4">{versionForm.updated_doc.name}</span>}
                          <input type="file" className="hidden" onChange={e => setVersionForm(prev => ({ ...prev, updated_doc: e.target.files[0] }))} />
                        </label>
                      </div>
                    </div>

                    <div className="pt-4">
                      <button 
                        disabled={submitting}
                        onClick={handleVersionSubmit}
                        className="w-full bg-red-700 hover:bg-red-800 text-white py-4 rounded-3xl text-xs font-black uppercase tracking-widest shadow-2xl shadow-red-750/30 disabled:opacity-50 transition-all active:scale-95"
                      >
                        {submitting ? 'Applying Version...' : 'Apply Version Update'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

function SidebarRows({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">{label}</p>
      <div className="text-xs font-bold text-gray-700">{value}</div>
    </div>
  );
}

function SidebarRowVertical({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-4 text-xs border-b border-gray-50 pb-2 last:border-0 last:pb-0">
      <span className="text-gray-500 font-medium shrink-0">{label} :</span>
      <span className="text-gray-800 font-bold text-right break-all">{value}</span>
    </div>
  );
}
